// @ts-nocheck
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { sanitizeTelephone } from '../../helpers'
import { appsRecoverPasswordSelected } from '../../helpers/app'
import { urls } from '../../routing'
import { AuthenticationService, LiteralsService } from '../../services'
import {
  checkSessionAction,
  recoverPasswordFailedAction,
  requestRecoverCodeFailedAction,
  requestRecoverCodeSuccessfulAction,
  setIsRecoveringPasswordAction,
  setIsRequestingRecoverCodeAction,
  setNotificationAction,
  setVerificationDNIAction,
  setVerificationEmailAction,
  setVerificationOriginAction
} from '../actions'
import { IStoreState } from '../states'
import { goToAuthScreen } from './auth-navigation'
import { RecoverPasswordTypeKeys } from '../types'

function isForceChangePasswordError(error: any): boolean {
  const name = error?.name || error?.code || error?.underlyingError?.name
  const message: string = error?.message || error?.underlyingError?.message || ''
  return name === 'NotAuthorizedException' && /current state/i.test(message)
}

function* handleRecoverError(error: any): any {
  if (isForceChangePasswordError(error)) {
    yield put(setNotificationAction({
      isError: true,
      label: LiteralsService.get('forceChangePasswordContactSupport', true)
    }))
    return
  }
  const errorKey = error?.name || error?.code || error?.underlyingError?.name
  yield put(setNotificationAction({
    isError: true,
    label: LiteralsService.getOrDefault(errorKey, 'serverError', true)
  }))
}

function deriveCognitoUsernameForRecover(email: string): string | null {
  const trimmed = email.trim()
  return trimmed.includes('@') ? trimmed.split('@')[0] : trimmed
}

function* tryResendForUnconfirmedAccount(): any {
  const { email } = yield select((state: IStoreState) => state.recoverPassword)
  const cognitoUsername = deriveCognitoUsernameForRecover(email)
  if (!cognitoUsername) return false
  try {
    yield call(AuthenticationService.resendSignUpConfirmationCode, cognitoUsername)
  } catch (resendError: any) {
    return false
  }
  yield put(setVerificationEmailAction((email || '').trim()))
  yield put(setVerificationDNIAction(cognitoUsername))
  yield put(setVerificationOriginAction('recoverPassword'))
  yield put(setNotificationAction({
    isError: false,
    label: LiteralsService.get('verificationCodeSent', true)
  }))
  yield* goToAuthScreen(urls.accountVerificationStep)
  return true
}

function* deriveRecoverIdentifier(): any {
  const { email, phone, callingCode } = yield select((state: IStoreState) => state.recoverPassword)
  if (appsRecoverPasswordSelected?.username === 'phone') return sanitizeTelephone((phone || '').trim(), callingCode)
  return email
}

function isUnconfirmedRecoverError(error: any): boolean {
  const name = error?.name || error?.code || error?.underlyingError?.name
  const message: string = error?.message || error?.underlyingError?.message || ''
  if (name === 'UserNotFoundException') return true
  if (name === 'InvalidParameterException' && /no registered\/verified|no registered or verified/i.test(message)) return true
  return false
}

// Worker Saga
function* requestCodeAsync() : any {

  try {
    yield put(setIsRequestingRecoverCodeAction())
    const identifier = yield* deriveRecoverIdentifier()
    yield call(
      AuthenticationService.requestRecoverPasswordCode,
      identifier
    )
    yield put(requestRecoverCodeSuccessfulAction())
    yield put(
      setNotificationAction({
        isError: false,
        label: LiteralsService.get('verificationCodeSent', true)
      })
    )
    yield* goToAuthScreen(urls.recoverPasswordRestoreStep)
  } catch (error: any) {
    if (isUnconfirmedRecoverError(error)) {
      const recovered: boolean = yield* tryResendForUnconfirmedAccount()
      if (recovered) {
        yield put(requestRecoverCodeSuccessfulAction())
        return
      }
    }
    yield put(requestRecoverCodeFailedAction(error.status))
    yield* handleRecoverError(error)
    
  }
}

function* recoverPasswordAsync() : any {

  try {
    yield put(setIsRecoveringPasswordAction())
    const { verificationCode, newPassword } = yield select(
      (state: IStoreState) => state.recoverPassword
    )
    const identifier = yield* deriveRecoverIdentifier()

    yield call(
      AuthenticationService.requestRecoverPasswordChange,
      identifier,
      verificationCode,
      newPassword
    )
    try {
      yield call(AuthenticationService.signIn, {
        username: identifier,
        password: newPassword
      })
      yield put(checkSessionAction())
    } catch (error: any) {
      const errorKey = error?.name || error?.code || error?.underlyingError?.name
      yield put(setNotificationAction({
        isError: true,
        label: LiteralsService.getOrDefault(errorKey, 'serverError', true),
      }))
      yield put(recoverPasswordFailedAction(errorKey || ''))
      yield* goToAuthScreen(urls.signIn)
      return
    }
  } catch (error: any) {
    yield put(recoverPasswordFailedAction(error.code))
    yield* handleRecoverError(error)
  }
}

// Watcher Saga:
function* watchRequestCodeAsync() {
  yield takeLatest(RecoverPasswordTypeKeys.REQUEST_RECOVER_CODE, requestCodeAsync)
}

// Watcher Saga:
function* watchRecoverPasswordAsync() {
  yield takeLatest(RecoverPasswordTypeKeys.RECOVER_PASSWORD, recoverPasswordAsync)
}

// single entry point to start all Sagas at once
export default function* rootSaga() {
  yield all([watchRequestCodeAsync(), watchRecoverPasswordAsync()])
}
